import Phaser from 'phaser';
import { PALETTE, TEXT_COLOUR, FONT, px } from '../theme';

/**
 * Ein Texteingabefeld.
 *
 * Gezeichnet wird im Canvas, damit es aussieht wie der Rest. Die Eingabe selbst
 * laeuft aber ueber ein unsichtbares DOM-`<input>`: Nur ein echtes Eingabefeld
 * oeffnet auf Telefon und Tablet die Bildschirmtastatur, und nur dort
 * funktionieren Autokorrektur, Umlaute ueber lange Tastendruecke und
 * Diktierfunktion. Frueher wurde die Tastatur der Szene abgehoert — auf einem
 * Handy liess sich der Name dadurch ueberhaupt nicht aendern.
 */

export interface TextInputOptions {
  readonly width: number;
  readonly value: string;
  readonly maxLength?: number;
  readonly fontSize?: number;
  readonly onChange: (value: string) => void;
}

export interface TextInput {
  readonly container: Phaser.GameObjects.Container;
  readonly focus: () => void;
  readonly blur: () => void;
  readonly destroy: () => void;
}

/** Zeichen, die in einem Namen vorkommen duerfen. */
const ALLOWED = /[^\p{L}\p{M} '\-.]/gu;

export function createTextInput(
  scene: Phaser.Scene,
  x: number,
  y: number,
  options: TextInputOptions,
): TextInput {
  const height = 44;
  const maxLength = options.maxLength ?? 28;
  const fontSize = options.fontSize ?? 17;

  let value = options.value;
  let focused = false;

  const frame = scene.add.rectangle(0, 0, options.width, height, PALETTE.panel, 0.6).setOrigin(0, 0);
  frame.setStrokeStyle(1, PALETTE.panelEdge);

  const label = scene.add.text(10, height / 2, value, {
    fontFamily: FONT.display,
    fontSize: px(fontSize),
    color: PALETTE.ink,
  });
  label.setOrigin(0, 0.5);

  const caret = scene.add.rectangle(0, height / 2, 1.5, fontSize + 4, PALETTE.coast).setOrigin(0, 0.5);
  caret.setVisible(false);

  const container = scene.add.container(x, y, [frame, label, caret]);
  container.setSize(options.width, height);
  container.setInteractive(
    new Phaser.Geom.Rectangle(options.width / 2, height / 2, options.width, height),
    Phaser.Geom.Rectangle.Contains,
  );
  container.input!.cursor = 'text';

  /*
   * Das DOM-Feld liegt deckungsgleich ueber dem gezeichneten, aber unsichtbar.
   *
   * Deckungsgleich, damit mobile Browser beim Fokussieren an die richtige
   * Stelle scrollen. 16 px Schrift, weil iOS bei allem darunter die Seite
   * heranzoomt — und danach nicht wieder heraus.
   */
  const dom = document.createElement('input');
  dom.type = 'text';
  dom.value = value;
  dom.maxLength = maxLength;
  dom.autocomplete = 'off';
  dom.spellcheck = false;
  dom.setAttribute('autocapitalize', 'words');
  dom.setAttribute('enterkeyhint', 'done');
  dom.setAttribute('aria-label', 'Name');
  Object.assign(dom.style, {
    position: 'fixed',
    opacity: '0',
    fontSize: '16px',
    border: '0',
    padding: '0',
    margin: '0',
    background: 'transparent',
    color: 'transparent',
    caretColor: 'transparent',
    zIndex: '10',
    pointerEvents: 'none',
  } satisfies Partial<CSSStyleDeclaration>);
  document.body.appendChild(dom);

  const placeDom = (): void => {
    const canvas = scene.game.canvas.getBoundingClientRect();
    const scaleX = canvas.width / scene.scale.width;
    const scaleY = canvas.height / scene.scale.height;
    // Die tatsaechliche Lage — das Feld kann in einem Scrollbereich stecken.
    const bounds = container.getBounds();
    dom.style.left = `${canvas.left + bounds.x * scaleX}px`;
    dom.style.top = `${canvas.top + bounds.y * scaleY}px`;
    dom.style.width = `${options.width * scaleX}px`;
    dom.style.height = `${height * scaleY}px`;
  };

  const positionCaret = (): void => {
    const cursor = dom.selectionStart ?? value.length;
    // Die Schreibmarke steht dort, wo sie im DOM-Feld steht — auch mitten im Wort.
    const before = label.text.slice(0, cursor);
    const probe = scene.add.text(0, 0, before, label.style).setVisible(false);
    caret.x = 10 + probe.width + 1;
    probe.destroy();
  };

  const render = (): void => {
    label.setText(value);
    positionCaret();
  };

  const blink = scene.time.addEvent({
    delay: 500,
    loop: true,
    callback: () => caret.setVisible(focused && !caret.visible),
  });

  const setFocused = (next: boolean): void => {
    if (next === focused) return;
    focused = next;
    frame.setStrokeStyle(1, next ? PALETTE.coast : PALETTE.panelEdge);
    caret.setVisible(next);
    label.setColor(next ? TEXT_COLOUR.coast : PALETTE.ink);
    if (next) {
      placeDom();
      dom.focus({ preventScroll: true });
      dom.setSelectionRange(value.length, value.length);
      render();
    } else if (document.activeElement === dom) {
      dom.blur();
    }
  };

  const onInput = (): void => {
    const cleaned = dom.value.replace(ALLOWED, '').slice(0, maxLength);
    if (cleaned !== dom.value) dom.value = cleaned;
    value = cleaned;
    render();
    options.onChange(value);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    // Die Taste gehoert dem Feld, nicht den Tastenkuerzeln der Szene.
    event.stopPropagation();
    if (event.key === 'Enter' || event.key === 'Escape') setFocused(false);
  };

  const onDomBlur = (): void => setFocused(false);
  const onSelect = (): void => positionCaret();

  dom.addEventListener('input', onInput);
  dom.addEventListener('keydown', onKeyDown);
  dom.addEventListener('keyup', onSelect);
  dom.addEventListener('blur', onDomBlur);

  // Der Fokus muss innerhalb der Nutzerhandlung gesetzt werden — iOS oeffnet
  // die Tastatur sonst nicht. Phaser ruft `pointerdown` synchron aus dem
  // DOM-Ereignis heraus auf, das genuegt.
  container.on('pointerdown', () => setFocused(true));

  // Ein Tippen daneben beendet die Eingabe.
  const onScenePointer = (pointer: Phaser.Input.Pointer): void => {
    if (!container.getBounds().contains(pointer.x, pointer.y)) setFocused(false);
  };
  scene.input.on('pointerdown', onScenePointer);

  render();

  let destroyed = false;
  // Raeumt alles ausserhalb des Containers ab. Der Container selbst wird nur
  // auf ausdruecklichen Wunsch zerstoert — laeuft das hier aus seinem eigenen
  // DESTROY-Ereignis, haette ein zweites destroy() Phaser mitten im Abbau
  // getroffen und den Neuaufbau der Szene abgebrochen.
  const cleanup = (): void => {
    if (destroyed) return;
    destroyed = true;
    blink.destroy();
    scene.input.off('pointerdown', onScenePointer);
    scene.events.off(Phaser.Scenes.Events.SHUTDOWN, cleanup);
    dom.removeEventListener('input', onInput);
    dom.removeEventListener('keydown', onKeyDown);
    dom.removeEventListener('keyup', onSelect);
    dom.removeEventListener('blur', onDomBlur);
    dom.remove();
  };
  const destroy = (): void => {
    cleanup();
    if (container.active) container.destroy();
  };

  // Das DOM-Feld lebt ausserhalb der Szene und muss mit ihr verschwinden —
  // sonst bliebe es nach einem Szenenwechsel fokussierbar im Dokument.
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, cleanup);
  container.once(Phaser.GameObjects.Events.DESTROY, cleanup);

  return {
    container,
    focus: () => setFocused(true),
    blur: () => setFocused(false),
    destroy,
  };
}
